// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useBlocker, useNavigate } from "@tanstack/react-router";
import { Fragment, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type UserRow, useUserByPublicIdQuery } from "@/api/hooks/use-users";
import { PageHeader } from "@/components/layout/page-header";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { StoreLink } from "@/components/shared/store-link";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { UserLink } from "@/components/shared/user-link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChangeStoreDialog } from "@/features/iam/change-store-dialog";
import { DeleteUserDialog } from "@/features/iam/delete-user-dialog";
import { RealmBadge } from "@/features/iam/realm-badge";
import { ToggleUserActiveDialog } from "@/features/iam/toggle-user-active-dialog";
import { UserActionsMenu } from "@/features/iam/user-actions-menu";
import { UserActivityTab } from "@/features/iam/user-activity-tab";
import { UserBoPermissionsTab, type UserBoPermissionsTabHandle } from "@/features/iam/user-bo-permissions-tab";
import { UserEditTab, type UserEditTabHandle } from "@/features/iam/user-edit-tab";
import { UserPermissionsTab, type UserPermissionsTabHandle } from "@/features/iam/user-permissions-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";

interface UserDetailProps {
  publicId: string;
  tab?: string;
  initialFilters?: string;
}

export function UserDetail({ publicId, tab, initialFilters }: UserDetailProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();

  const { data, isLoading, isError } = useUserByPublicIdQuery(publicId);
  const { user: currentUser, hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  const isAdmin = currentUser?.groups.includes("admin") ?? false;
  // A deleted user is read-only — hide the mutating tabs
  const canEdit = hasPermission(PAGE_PERMISSIONS.iamUsersEdit) && !data?.user.isDeleted;

  // Fall back to Info when an Edit tab is requested without permission
  const requestedTab = tab ?? "info";
  const tabAllowed: Record<string, boolean> = {
    info: true,
    edit: canEdit,
    permissions: canEdit,
    "bo-permissions": canEdit,
    // Activity log is work-in-progress — debug-mode only.
    activity: debugMode,
  };
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  const [toggleUser, setToggleUser] = useState<UserRow | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserRow | null>(null);
  const [changeStoreUser, setChangeStoreUser] = useState<{
    publicId: string;
    email: string;
    fullName?: string;
    defaultStoreName: string;
  } | null>(null);

  // Unsaved-changes guard for the editable tabs
  const [isEditDirty, setIsEditDirty] = useState(false);
  const [isPermissionsDirty, setIsPermissionsDirty] = useState(false);
  const [isBoPermissionsDirty, setIsBoPermissionsDirty] = useState(false);
  const editTabRef = useRef<UserEditTabHandle>(null);
  const permissionsTabRef = useRef<UserPermissionsTabHandle>(null);
  const boPermissionsTabRef = useRef<UserBoPermissionsTabHandle>(null);
  const isFormDirty = isEditDirty || isPermissionsDirty || isBoPermissionsDirty;
  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: () => isFormDirty,
    withResolver: true,
  });

  const breadcrumbs = useMemo(() => [{ label: t("users.title"), to: "/iam/users" }], [t]);

  function handleTabChange(newTab: string) {
    navigate({
      to: "/iam/users/$publicId",
      params: { publicId },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <PageHeader title={publicId} breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  const { user, groups, defaultStore, availableStores, permissions, flags, supervisor } = data;
  // Directly-assigned permissions first, then the group-sourced ones
  const orderedPermissions = [
    ...permissions.filter((source) => source.groupName === null),
    ...permissions.filter((source) => source.groupName !== null),
  ];

  return (
    <div>
      <PageHeader
        title={user.email}
        subtitle={
          <>
            <IdPill id={user.userId} />
            <StatusIndicator active={user.isActive} deleted={user.isDeleted} />
            {!user.isDeleted && (
              <UserActionsMenu
                isActive={user.isActive}
                isAdmin={isAdmin}
                isSelf={currentUser?.publicId === user.publicId}
                onChangeStore={() => {
                  setChangeStoreUser({
                    publicId: user.publicId,
                    email: user.email,
                    fullName: user.fullName,
                    defaultStoreName: defaultStore?.storeName ?? "",
                  });
                }}
                onToggleActive={() => setToggleUser(user)}
                onDelete={() => setDeleteUser(user)}
                triggerClassName="ml-auto -my-1.5"
              />
            )}
          </>
        }
        breadcrumbs={breadcrumbs}
      />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("tabs.info")}</TabsTrigger>
              {canEdit && <TabsTrigger value="edit">{t("tabs.edit")}</TabsTrigger>}
              {canEdit && <TabsTrigger value="permissions">{t("tabs.permissions")}</TabsTrigger>}
              {canEdit && <TabsTrigger value="bo-permissions">{t("tabs.boPermissions")}</TabsTrigger>}
              {debugMode && <TabsTrigger value="activity">{t("tabs.activityLog")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <div className="max-w-2xl space-y-6 p-6">
              <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
                {user.fullName && (
                  <>
                    <span className="text-muted-foreground">{t("users.detail.fullName")}</span>
                    <span>{user.fullName}</span>
                  </>
                )}

                <span className="text-muted-foreground">{t("users.list.columns.email")}</span>
                <span>{user.email}</span>

                {user.userName && (
                  <>
                    <span className="text-muted-foreground">{t("users.list.columns.userName")}</span>
                    <span>{user.userName}</span>
                  </>
                )}

                <span className="text-muted-foreground">{t("users.list.columns.publicId")}</span>
                <span>
                  <KeyChip label={user.publicId} />
                </span>

                <span className="text-muted-foreground">{t("users.list.columns.realm")}</span>
                <span>
                  <RealmBadge realm={user.realm} />
                </span>

                <span className="text-muted-foreground">{t("users.list.columns.mfa")}</span>
                <Switch checked={user.hasMfa} tabIndex={-1} aria-readonly className="pointer-events-none" />

                <Separator className="col-span-2" />

                <span className="text-muted-foreground">{t("users.list.columns.defaultStore")}</span>
                <span>
                  {defaultStore ? (
                    <StoreLink storeName={defaultStore.storeName} legibleName={defaultStore.legibleName} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </span>

                <span className="text-muted-foreground">{t("users.detail.availableStores")}</span>
                <span>
                  {availableStores.length > 0 ? (
                    <span className="flex flex-wrap gap-x-3 gap-y-1.5">
                      {availableStores.map((store) => (
                        <StoreLink key={store.storeId} storeName={store.storeName} legibleName={store.legibleName} />
                      ))}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </span>

                <span className="text-muted-foreground">{t("users.list.columns.lastLoginAt")}</span>
                <DateTimeValue value={user.lastLoginAt} />

                <Separator className="col-span-2" />

                <span className="text-muted-foreground">{t("users.detail.groupsTitle")}</span>
                <span>
                  {groups.length > 0 ? (
                    <span className="flex flex-wrap gap-1.5">
                      {groups.map((group) => (
                        <Badge key={group} variant="secondary">
                          {group}
                        </Badge>
                      ))}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </span>

                {supervisor && (
                  <>
                    <span className="text-muted-foreground">{t("users.form.supervisor")}</span>
                    <span>
                      <UserLink publicId={supervisor.publicId} legibleName={supervisor.fullName || supervisor.email} />
                    </span>
                  </>
                )}

                {flags.length > 0 && (
                  <>
                    <span className="text-muted-foreground">{t("users.form.flags")}</span>
                    <span className="flex flex-wrap gap-1.5">
                      {flags.map((flag) => (
                        <Badge key={flag} variant="outline" className="font-mono font-normal">
                          {flag}
                        </Badge>
                      ))}
                    </span>
                  </>
                )}
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-medium">{t("users.detail.permissionsTitle")}</h3>
                {permissions.length > 0 ? (
                  <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
                    {orderedPermissions.map((source, index) => (
                      <Fragment key={source.groupName ?? "__direct__"}>
                        {index > 0 &&
                          source.groupName !== null &&
                          orderedPermissions[index - 1]?.groupName === null && <Separator className="col-span-2" />}
                        <span className="text-muted-foreground">
                          {source.groupName ?? t("users.detail.directlyAssigned")}
                        </span>
                        <span className="flex flex-wrap gap-1.5">
                          {[...new Set(source.permissions)].map((permission) => (
                            <Badge key={permission} variant="outline" className="font-mono font-normal">
                              {permission}
                            </Badge>
                          ))}
                        </span>
                      </Fragment>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">—</p>
                )}
              </div>

              <div className="-mt-3 grid grid-cols-[160px_1fr] gap-y-3 text-sm">
                <Separator className="col-span-2" />

                <span className="text-muted-foreground">{t("common.createdAt")}</span>
                <DateTimeValue value={user.createdAt} />

                <span className="text-muted-foreground">{t("common.updatedAt")}</span>
                <DateTimeValue value={user.updatedAt} />
              </div>
            </div>
          </TabsContent>
          {canEdit && (
            <TabsContent value="edit" className="space-y-4">
              <UserEditTab ref={editTabRef} publicId={publicId} onDirtyChange={setIsEditDirty} />
            </TabsContent>
          )}
          {canEdit && (
            <TabsContent value="permissions" className="space-y-4">
              <UserPermissionsTab ref={permissionsTabRef} publicId={publicId} onDirtyChange={setIsPermissionsDirty} />
            </TabsContent>
          )}
          {canEdit && (
            <TabsContent value="bo-permissions" className="space-y-4">
              <UserBoPermissionsTab
                ref={boPermissionsTabRef}
                publicId={publicId}
                onDirtyChange={setIsBoPermissionsDirty}
              />
            </TabsContent>
          )}
          {debugMode && (
            <TabsContent value="activity" className="space-y-4">
              <UserActivityTab
                publicId={publicId}
                initialFilters={activeTab === "activity" ? initialFilters : undefined}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <ChangeStoreDialog user={changeStoreUser} onClose={() => setChangeStoreUser(null)} />
      <ToggleUserActiveDialog user={toggleUser} onClose={() => setToggleUser(null)} />
      <DeleteUserDialog user={deleteUser} onClose={() => setDeleteUser(null)} />
      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          editTabRef.current?.reset();
          permissionsTabRef.current?.reset();
          boPermissionsTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
